import { useEffect, useRef, useState } from 'react';
import { useMemo } from 'react';
import PropTypes from 'prop-types';

// Função recursiva para extrair textos de qualquer bloco, incluindo columns
const extractTextFromBlock = (block, properties) => {
  if (!block) return '';
  switch (block['@type']) {
    case 'title':
      return properties.title || '';
    case 'subtitleBlock':
      return properties.subtitle || '';
    case 'description':
      return properties.description || '';
    case 'slate':
      return block.plaintext || '';
    case 'SectionTitleBlock':
      return block.title || '';
    case 'SectionSubtitleBlock':
      return block.subtitle || '';
    case 'SectionDescriptionBlock':
      return block.description || '';
    case 'columnsBlock':
      if (block.data && block.data.blocks && block.data.blocks_layout) {
        return block.data.blocks_layout.items
          .map((colId) => {
            const col = block.data.blocks[colId];
            if (!col || !col.blocks_layout || !col.blocks) return '';
            return col.blocks_layout.items
              .map((colBlockId) =>
                extractTextFromBlock(col.blocks[colBlockId], properties),
              )
              .filter(Boolean)
              .join('. ');
          })
          .filter(Boolean)
          .join('. ');
      }
      return '';
    default:
      return '';
  }
};

const getTextFromBlocks = (blocks, properties) => {
  if (!blocks || !properties) return '';
  const order = properties.blocks_layout?.items || [];
  return order
    .map((blockId) => extractTextFromBlock(blocks[blockId], properties))
    .filter(Boolean)
    .join('. ');
};

const TextToSpeechBody = ({ properties, isEditMode }) => {
  const [speaking, setSpeaking] = useState(false);
  const [speedRate, setSpeedRate] = useState(1.0);
  const speedOptions = [0.8, 1.0, 1.2, 1.4];
  const synthRef = useRef(
    typeof window !== 'undefined' ? window.speechSynthesis : null,
  );
  const utteranceRef = useRef(null);

  // Garante renderização apenas no client
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel(); // Garante que não há fala ativa ao montar
    }
  }, []);

  // Extrai o texto apenas quando properties mudar
  const text = useMemo(
    () => getTextFromBlocks(properties?.blocks, properties),
    [properties],
  );

  if (!isClient || !window.speechSynthesis) {
    return null;
  }

  const handlePlay = () => {
    // Cancela qualquer fala atual antes de iniciar uma nova
    if (synthRef.current) {
      synthRef.current.cancel();
    }

    // Cria nova instância de fala e inicia
    const utter = new window.SpeechSynthesisUtterance(text);
    utter.lang = 'pt-BR';
    // Aplica a velocidade selecionada
    utter.rate = speedRate;
    utter.onend = () => {
      setSpeaking(false);
    };
    utter.onerror = () => {
      setSpeaking(false);
    };
    utteranceRef.current = utter;
    synthRef.current.speak(utter);
    setSpeaking(true);
  };

  const handleStop = () => {
    if (synthRef.current && synthRef.current.speaking) {
      synthRef.current.cancel();
      setSpeaking(false);
    }
  };

  const handleSpeedChange = () => {
    // Para a reprodução atual se estiver tocando
    if (synthRef.current && synthRef.current.speaking) {
      synthRef.current.cancel();
      setSpeaking(false);
    }

    // Alterna para a próxima velocidade na lista de opções
    const currentIndex = speedOptions.indexOf(speedRate);
    const nextIndex = (currentIndex + 1) % speedOptions.length;
    setSpeedRate(speedOptions[nextIndex]);
  };

  return (
    <div className="d-flex justify-content-end mb-3x">
      <div className="d-flex align-items-baseline p-3 rounder-lg bg-gray-10">
        <i
          className="fa-solid fa-volume-high text-up-01 mr-2"
          aria-hidden="true"
        ></i>
        <div className="text-weight-semi-bold text-up-02 mr-2">
          Leitura por voz
        </div>
        <button
          onClick={speaking ? handleStop : handlePlay}
          disabled={isEditMode}
          className={'br-button primary circle small mr-2'}
          title={speaking ? 'Parar leitura' : 'Ouvir notícia'}
          aria-label={speaking ? 'Parar leitura' : 'Ouvir notícia'}
        >
          <i
            className={speaking ? 'fa-solid fa-stop' : 'fa-solid fa-play'}
            aria-hidden="true"
          ></i>
        </button>
        <button
          onClick={handleSpeedChange}
          disabled={isEditMode}
          className={'br-button secondary circle small'}
          title={'Alterar velocidade de reprodução para ' + speedRate + 'x'}
          aria-label={
            'Alterar velocidade de reprodução para ' + speedRate + 'x'
          }
        >
          <span className="text-bold text-base">{speedRate}x</span>
        </button>
      </div>
    </div>
  );
};

TextToSpeechBody.propTypes = {
  data: PropTypes.object,
  properties: PropTypes.object,
  isEditMode: PropTypes.bool,
};

export default TextToSpeechBody;
